import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  User,
  Mail,
  Phone,
  Briefcase,
  Users,
  type LucideIcon,
} from "lucide-react";
import { SlikeLogo } from "@/components/SlikeLogo";
import { authApi } from "@/api/auth";
import { NetworkError } from "@/api/network";
import { RequestAccessData, Business, Team } from "@/types/requestAccess";
import { PLATFORMS, DEFAULT_PLATFORMS, PlatformId } from "@/constants/platforms";
import { useAppSelector } from "@/store/hooks";

// ============================================================================
// TYPES & SCHEMAS
// ============================================================================

const ROLES = [
  { value: "business", label: "Business" },
  { value: "editor", label: "Editor" },
  { value: "product", label: "Product" },
] as const;

const requestAccessSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(3, { message: "Full name must be at least 3 characters" })
    .max(100, { message: "Full name must be less than 100 characters" })
    .regex(/^[a-zA-Z\s'-]+$/, {
      message: "Full name can only contain letters, spaces, hyphens, and apostrophes",
    }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .max(255, { message: "Email must be less than 255 characters" })
    .toLowerCase(),
  mobile: z
    .string()
    .trim()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const digitsOnly = val.replace(/\D/g, '');
        if (digitsOnly.length !== 10) return false;
        if (!/^[\d\s+\-()]+$/.test(val)) return false;
        return true;
      },
      { message: "Mobile number must be exactly 10 digits" }
    ),
  role: z.string().min(1, { message: "Please select a role" }),
  team: z.string().min(1, { message: "Please select a team" }),
  platforms: z
    .object({
      cms: z.boolean().default(false),
      analytics: z.boolean().default(false),
      live: z.boolean().default(false),
    })
    .refine(
      (data) => Object.values(data).some((value) => value === true),
      { message: "Please select at least one platform" }
    ),
});

type RequestAccessFormValues = z.infer<typeof requestAccessSchema>;

const DEFAULT_FORM_VALUES: RequestAccessFormValues = {
  fullName: "",
  email: "",
  mobile: "",
  role: "",
  team: "",
  platforms: DEFAULT_PLATFORMS as Record<PlatformId, boolean>,
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const LoadingState = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-background p-4">
    <div className="flex items-center gap-2">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
      <span className="text-muted-foreground">Loading...</span>
    </div>
  </div>
);

const PageHeader = ({ requestedApp }: { requestedApp?: string }) => (
  <div className="text-center mb-8">
    <div className="flex justify-center mb-6">
      <SlikeLogo
        iconClassName="h-10"
        textClassName="text-4xl"
      />
    </div>
    <h1 className="text-3xl font-bold mb-2">Create Account</h1>
    <p className="text-muted-foreground">
      {requestedApp 
        ? `Request access to ${requestedApp} and other platforms`
        : "Request access to the platform"}
    </p>
  </div>
);

interface InputWithIconProps {
  icon: LucideIcon;
  type?: string;
  placeholder: string;
  className?: string;
  field: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
  };
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
}

const InputWithIcon = ({ icon: Icon, type, placeholder, className = "pl-10 h-12", field, inputRef, autoFocus }: InputWithIconProps) => {
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData("text").trim();
    if (pastedValue) {
      const syntheticEvent = {
        target: { value: pastedValue }
      } as React.ChangeEvent<HTMLInputElement>;
      field.onChange(syntheticEvent);
    }
  };

  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
      <Input ref={inputRef} type={type} placeholder={placeholder} className={className} {...field} onPaste={handlePaste} autoFocus={autoFocus} />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RequestAccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const fullNameInputRef = useRef<HTMLInputElement>(null);

  const locationState = location.state as { appName?: string; redirectUrl?: string } | null;
  const requestedAppName = locationState?.appName;
  const redirectUrl = locationState?.redirectUrl;

  // Redirect to /home if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/home", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Determine default platforms based on requested app
  const getDefaultPlatforms = (): Record<PlatformId, boolean> => {
    if (!requestedAppName) {
      return DEFAULT_PLATFORMS as Record<PlatformId, boolean>;
    }

    const appNameLower = requestedAppName.toLowerCase();
    const platforms: Record<PlatformId, boolean> = {
      cms: false,
      analytics: false,
      live: false,
    };

    if (appNameLower.includes("cms")) {
      platforms.live = true;
    } else if (appNameLower.includes("app") || appNameLower.includes("chopper") || 
        appNameLower.includes("optimus")) {
      platforms.cms = true;
    }
    if (appNameLower.includes("analytics")) {
      platforms.analytics = true;
    }

    // If no specific platform matched, default to CMS
    if (!platforms.cms && !platforms.analytics && !platforms.live) {
      platforms.cms = true;
    }

    return platforms;
  };

  const form = useForm<RequestAccessFormValues>({
    resolver: zodResolver(requestAccessSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      platforms: getDefaultPlatforms(),
    },
  });

  const selectedTeamId = form.watch("team");
  const selectedRole = form.watch("role");
  const platforms = form.watch("platforms");
  const fullName = form.watch("fullName");
  const email = form.watch("email");
  const { isValid, isSubmitting } = form.formState;

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const allTeams = useMemo(() => {
    return businesses
      .flatMap((b) =>
        b.teams
          ?.filter((t) => t.archive !== 1)
          .map((t) => ({ ...t, businessId: b.id, businessName: b.name })) || []
      )
      .sort((a, b) =>
        (b.last_7d_video_count ?? 0) - (a.last_7d_video_count ?? 0) ||
        (b.video_count ?? 0) - (a.video_count ?? 0) ||
        (b.last_video_ct ?? 0) - (a.last_video_ct ?? 0)
      );
  }, [businesses]);

  // Derive business ID from selected team
  const derivedBusinessId = useMemo(() => {
    if (!selectedTeamId) return "";
    const team = allTeams.find((t) => t.id === selectedTeamId);
    return team?.businessId || "";
  }, [selectedTeamId, allTeams]);

  const canSubmit = isValid && !isSubmitting;
  const isFormReady =
    fullName?.trim().length > 0 &&
    email?.trim().length > 0 &&
    selectedRole?.trim().length > 0 &&
    selectedTeamId?.trim().length > 0 &&
    Object.values(platforms || {}).some(value => value === true);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await authApi.getTeams();
        if (response.data) {
          const validBusinesses = response.data.filter(
            (b) => b.name?.trim() && b.teams?.some(t => t.archive !== 1)
          );
          setBusinesses(validBusinesses);
        }
      } catch (error) {
        toast.error("Failed to load team options");
      }
    };

    fetchTeams();
  }, []);

  // Focus full name input on mount
  useEffect(() => {
    fullNameInputRef.current?.focus();
  }, []);

  // ============================================================================
  // CALLBACKS
  // ============================================================================



  const onSubmit = useCallback(
    async (data: RequestAccessFormValues) => {
      try {
        const platforms: { cms: boolean; analytics: boolean; live: boolean } = {
          cms: data.platforms.cms ?? false,
          analytics: data.platforms.analytics ?? false,
          live: data.platforms.live ?? false,
        };

        const cleanedMobile = data.mobile
          ? data.mobile.replace(/\D/g, '')
          : "";

        const businessId = derivedBusinessId;
        if (!businessId) {
          toast.error("Could not determine business for the selected team.");
          return;
        }

        const response = await authApi.requestAccess({
          fullName: data.fullName,
          email: data.email,
          mobile: cleanedMobile,
          business: businessId,
          teams: [data.team],
          defaultTeam: data.team,
          platforms,
        });

        if (response.success) {
          toast.success(response.message || "Your account request has been submitted. You will be notified once approved.");

          setTimeout(() => {
            form.reset(DEFAULT_FORM_VALUES);
            navigate("/");
          }, 3000);
        } else {
          toast.error(response.message || "Failed to create account");
        }
      } catch (error) {
        if (error instanceof NetworkError) {
          const data = (error.data as { msg?: string; message?: string }) || {};
          const apiMessage =
            typeof data.msg === "string"
              ? data.msg
              : typeof data.message === "string"
                ? data.message
                : error.message || "Failed to create account. Please try again.";
          toast.error(apiMessage);
        } else {
          toast.error("Failed to create account. Please try again.");
        }
      }
    },
    [navigate, form, derivedBusinessId]
  );

  const handleBackToLogin = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // ============================================================================
  // RENDER
  // ============================================================================



  return (

    <div className="w-full max-w-xl animate-fade-in">
      <PageHeader requestedApp={requestedAppName} />

      <Card className="glass-card p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {/* Personal Information */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name *</FormLabel>
                    <FormControl>
                      <InputWithIcon icon={User} placeholder="John Doe" field={field} inputRef={fullNameInputRef} autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address *</FormLabel>
                      <FormControl>
                        <InputWithIcon
                          icon={Mail}
                          type="email"
                          placeholder="john@company.com"
                          field={field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile Number (Optional)</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <span className="absolute left-10 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">+91</span>
                          <Input
                            type="tel"
                            placeholder="9876543210"
                            className="pl-[4.5rem] h-12"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Role & Team */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                            <SelectTrigger className="pl-10 h-12">
                              <SelectValue placeholder="Select role..." />
                            </SelectTrigger>
                          </div>
                        </FormControl>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="team"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Team *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <div className="relative">
                            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                            <SelectTrigger className="pl-10 h-12">
                              <SelectValue placeholder="Select team..." />
                            </SelectTrigger>
                          </div>
                        </FormControl>
                        <SelectContent>
                          {allTeams.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground">
                              No teams available
                            </div>
                          ) : (
                            allTeams.map((team) => (
                              <SelectItem key={team.id} value={team.id}>
                                {team.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Platform Access */}
            <div className="space-y-4 pt-4 border-t">
              <FormLabel>Platform Access *</FormLabel>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {PLATFORMS.map((platform) => (
                  <FormField
                    key={platform.id}
                    control={form.control}
                    name={`platforms.${platform.id}`}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg py-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="font-medium cursor-pointer flex items-center gap-2">
                            <platform.icon className="w-4 h-4 text-muted-foreground" />
                            {platform.label}
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormField
                control={form.control}
                name="platforms"
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-6 border-t">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </button>

              <Button
                type="submit"
                className="h-12 px-8 gradient-primary hover:opacity-90"
                disabled={isSubmitting || !isFormReady}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </Card>
    </div>

  );
};

export default RequestAccess;
